import { useEffect, useRef, VoidFunctionComponent } from 'react';
import { MeshBasicMaterial } from 'three';
import { useProgress, useTexture, Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useResponsive } from '@/hooks/useResponsive';
import { garamondNarrowLight } from '@/lib/fonts';
import cdLoadingIcon from '@/img/cd-loading-icon.png';
import { clampedLerp } from '@/lib/mathUtils';
import { useEditionPreloader } from '@/hooks/usePreloader';

interface DreiText {
    fillOpacity: number;
    text: string;
}

const COLOR = '#FFFFFF';
const CD_SCALAR = 0.3;
const SHOW_CD_DELAY = 1;
const SHOW_PERCENT_LOADED_DELAY = 3;
const LERP_INTERVAL = 0.5; // time to lerp to new progress value

interface Props {
    onLoaded: () => void;
    flowReady: boolean;
}

export const EditionLoading: VoidFunctionComponent<Props> = ({ onLoaded, flowReady }) => {
    useEditionPreloader();

    const textRef = useRef<DreiText>();
    const cdIconRef = useRef<MeshBasicMaterial>(null);
    const { progress, loaded, total } = useProgress();
    const { viewport } = useThree();
    const cdIcon = useTexture(cdLoadingIcon);
    const { width, height } = viewport.getCurrentViewport();
    const { responsiveValue } = useResponsive();
    const lastProgress = useRef<number>(0);
    const lerpedProgress = useRef<number>(0);
    const timer = useRef<number>(0);
    const lerpTimer = useRef<number>(0);
    const cdSpacer = responsiveValue(0.1, 0);

    // show pulsing CD icon after a delay, show text progress after a longer delay
    useFrame((_, delta) => {
        timer.current += delta;

        // reset lerpTimer when progress changes so we can lerp to the next progress number
        if (progress !== lastProgress.current) {
            lerpTimer.current = 0;
        }

        if (timer.current > SHOW_PERCENT_LOADED_DELAY) {
            lerpTimer.current += delta;
            if (textRef.current && textRef.current.fillOpacity === 0) {
                textRef.current.fillOpacity = 1;
            }

            lerpedProgress.current = clampedLerp(lerpedProgress.current, progress, lerpTimer.current / LERP_INTERVAL);

            if (textRef.current) {
                textRef.current.text = `${Math.round(lerpedProgress.current)}%`;
            }
        }

        // random opacity for cd loader icon
        if (timer.current > SHOW_CD_DELAY && cdIconRef.current) {
            cdIconRef.current.opacity = Math.random() * (0.5 - 0.2) + 0.2;
        }

        lastProgress.current = progress;
    });

    useEffect(() => {
        if (loaded === total && flowReady) {
            onLoaded();
        }
    }, [loaded, total, onLoaded, flowReady]);

    return (
        <group position={[width / 2 - CD_SCALAR - cdSpacer, -height / 2 + CD_SCALAR + cdSpacer, 0]}>
            <Text
                position={[-0.35, -0.03, 0]}
                font={garamondNarrowLight}
                fontSize={0.15}
                color={COLOR}
                anchorX="center"
                fillOpacity={0}
                ref={textRef}
            >
                0%
            </Text>
            <mesh>
                <planeGeometry attach="geometry" args={[CD_SCALAR, CD_SCALAR]} />
                <meshBasicMaterial ref={cdIconRef} color="white" map={cdIcon} transparent opacity={0} />
            </mesh>
        </group>
    );
};
